import Papa from 'papaparse';
import { Transaction, Account, Category } from '../types';

export class CSVService {
  // Export transactions to CSV
  exportTransactions(transactions: Transaction[], accounts: Account[], categories: Category[]): string {
    const data = transactions.map(transaction => {
      const account = accounts.find(a => a.id === transaction.accountId);
      const category = categories.find(c => c.id === transaction.categoryId);
      
      return {
        Date: transaction.date,
        Account: account?.name || 'Unknown',
        Payee: transaction.payee,
        Category: category?.name || 'Uncategorized',
        Amount: transaction.amount,
        Memo: transaction.memo || '',
        Cleared: transaction.cleared ? 'Yes' : 'No',
        Reconciled: transaction.reconciled ? 'Yes' : 'No'
      };
    });
    
    return Papa.unparse(data, {
      header: true,
      skipEmptyLines: true
    });
  }

  // Export accounts to CSV
  exportAccounts(accounts: Account[]): string {
    const data = accounts.map(account => ({
      Name: account.name,
      Type: account.type,
      Currency: account.currency,
      'Open Date': account.openDate,
      Status: account.status,
      Balance: account.balance || 0
    }));
    
    return Papa.unparse(data, {
      header: true,
      skipEmptyLines: true
    });
  }

  // Export categories to CSV
  exportCategories(categories: Category[]): string {
    const data = categories.map(category => ({
      Name: category.name,
      Group: category.group || '',
      Envelope: category.envelope ? 'Yes' : 'No',
      Rollover: category.rollover,
      Target: category.target || '',
      Hidden: category.isHidden ? 'Yes' : 'No'
    }));
    
    return Papa.unparse(data, {
      header: true,
      skipEmptyLines: true
    });
  }

  // Parse CSV file and return structured data
  parseCSV<T = any>(csvContent: string): Promise<T[]> {
    return new Promise((resolve, reject) => {
      Papa.parse(csvContent, {
        header: true,
        skipEmptyLines: true,
        transformHeader: (header: string) => header.trim(),
        complete: (results: any) => {
          if (results.errors.length > 0) {
            reject(new Error(`CSV parsing error: ${results.errors[0].message}`));
          } else {
            resolve(results.data as T[]);
          }
        },
        error: (error: any) => {
          reject(error);
        }
      });
    });
  }

  // Import transactions from CSV
  async importTransactions(csvContent: string, accounts: Account[], categories: Category[]): Promise<Omit<Transaction, 'id' | 'createdAt' | 'updatedAt' | 'createdBy'>[]> {
    const data = await this.parseCSV<{
      Date: string;
      Account: string;
      Payee: string;
      Category: string;
      Amount: string;
      Memo: string;
      Cleared?: string;
      Reconciled?: string;
    }>(csvContent);

    const transactions: Omit<Transaction, 'id' | 'createdAt' | 'updatedAt' | 'createdBy'>[] = [];
    const errors: string[] = [];

    data.forEach((row, index) => {
      try {
        // Find account
        const account = accounts.find(a => 
          a.name.toLowerCase() === row.Account?.toLowerCase()
        );
        if (!account) {
          errors.push(`Row ${index + 1}: Account "${row.Account}" not found`);
          return;
        }

        // Find category
        const category = categories.find(c => 
          c.name.toLowerCase() === row.Category?.toLowerCase()
        );

        // Parse amount
        const amount = parseFloat(row.Amount);
        if (isNaN(amount)) {
          errors.push(`Row ${index + 1}: Invalid amount "${row.Amount}"`);
          return;
        }

        // Validate date
        const date = new Date(row.Date);
        if (isNaN(date.getTime())) {
          errors.push(`Row ${index + 1}: Invalid date "${row.Date}"`);
          return;
        }

        transactions.push({
          date: row.Date,
          accountId: account.id,
          amount: amount,
          payee: row.Payee || 'Unknown',
          categoryId: category?.id,
          memo: row.Memo,
          tags: [],
          cleared: row.Cleared?.toLowerCase() === 'yes',
          reconciled: row.Reconciled?.toLowerCase() === 'yes'
        });
      } catch (error) {
        errors.push(`Row ${index + 1}: ${error}`);
      }
    });

    if (errors.length > 0) {
      throw new Error(`Import errors:\n${errors.join('\n')}`);
    }

    return transactions;
  }

  // Download CSV file
  downloadCSV(csvContent: string, filename: string): void {
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }
}

export const csvService = new CSVService();